import { Component, forwardRef, Input, OnInit, ViewChild } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

import { forkJoin, Observable } from 'rxjs';
import { map, filter, tap } from 'rxjs/operators';

import { ButtonTypesEnum } from '../../../../Common/Forms/enums/button-types.enum';
import { SourceChip } from '../../../../Common/Sources/models/source-chip.model';
import { Source } from '../../../../Common/Sources/models/source.model';
import { SourceService } from '../../../../Common/Sources/services/source.service';
import { CoreDialogService } from '../../../../Core/Dialogs/services/dialog.service';
import { LocalizationService } from '../../../../Core/Localization/services/localization.service';
import { TextInputComponent } from '../../../Forms/components/TextInput/text-input.component';

@Component({
    selector: 'agpr-search-source-picker',
    templateUrl: './search-source-picker.component.html',
    styleUrls: ['./search-source-picker.component.scss'],
    providers: [
        {
            provide: 'SourceService',
            useClass: SourceService
        },
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => SearchSourcePickerComponent),
            multi: true
        }
    ],
    standalone: false
})
export class SearchSourcePickerComponent implements OnInit, ControlValueAccessor {
    @Input() id: string = null;
    @Input() value: number[] = [];

    @ViewChild('textInput') textInput: TextInputComponent;

    public _onChange: Function;
    public _onTouched: Function;

    existingSourceChips: SourceChip[] = [];
    selectedSource: Source;
    availableSources: Source[] = [];
    filteredSources: Source[] = [];

    readonly buttonTypes = ButtonTypesEnum;

    sourceList$: Observable<Source[]>;

    constructor(
        public sourceService: SourceService,
        public coreDialogService: CoreDialogService,
        public localizationService: LocalizationService
    ) {}

    ngOnInit() {
        this.sourceList$ = this.sourceService.all$;
    }

    /**
     * Handles selection of a dropdown item
     */
    selectExistingSource(source: Source): void {
        if (!this.value.includes(source.id)) {
            this.addToValue(source.id);
            this.existingSourceChips.push(source.toChipModel());
        }
    }

    /**
     * Emits event to update the currently selected sources
     */
    existingSourceRemoved(chip: SourceChip): void {
        this.existingSourceChips = this.existingSourceChips.filter(source => source !== chip);
        this.removeFromValue(chip.id);
        this._onChange(this.value);
    }

    /**
     * Adds to value array
     */
    addToValue(sourceId: number): void {
        this.value = [...this.value, sourceId];
        this._onChange(this.value);
    }

    /**
     * Removes from value array
     */
    removeFromValue(sourceId: number): void {
        this.value = this.value.filter(id => id !== sourceId);
        this._onChange(this.value);
    }


    async writeValue(sourceIds: number[]) {
        if (!sourceIds) {
            return;
        }

        this.value = sourceIds;
        this.populateChips().subscribe();
    }

    /**
     * Fetches chip models for selected sourceIds
     */
    private populateChips(): Observable<SourceChip[]> {
        const requests = this.value.map(sourceId => {
            return this.sourceService.getById(sourceId).pipe(
                filter(x => !!x),
                map(source => source.toChipModel())
            );
        });

        return forkJoin(requests).pipe(
            tap(chips => this.existingSourceChips = chips)
        );
    }

    newSourcesAdded(ids: number[]) {
        this.value.push(...ids);
        this._onChange(this.value);
    }

    registerOnChange(onChange: Function) {
        this._onChange = onChange;
    }

    registerOnTouched(onTouched: Function) {
        this._onTouched = onTouched;
    }

}
